import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import authService from "../services/authService";
import { LOGO_SRC } from "../constants/branding";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Show the success state regardless of whether the email exists. Backend
  // returns 200 with a generic message for unknown / soft-deleted emails,
  // OR 200 with code='oauth_only_account' when the address is bound to a
  // Google-signup-only account (no password to reset). isOauthOnly switches
  // the success view to a "Use Sign in with Google" CTA instead of the
  // generic "check your inbox" message.
  const [submitted, setSubmitted] = useState(false);
  const [isOauthOnly, setIsOauthOnly] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setErrors({ email: "Email is required" });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      const data = await authService.requestPasswordReset(email);
      setIsOauthOnly(data?.code === "oauth_only_account");
      setSubmitted(true);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.message ||
        "Something went wrong. Please try again.";
      setErrors({ submit: detail });
      toast.error(detail);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[#131313] text-white">
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute -left-1/4 top-0 h-[min(70vh,520px)] w-[min(90vw,720px)] rounded-full bg-primary/[0.07] blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[min(50vh,400px)] w-[min(80vw,560px)] rounded-full bg-emerald-500/[0.05] blur-[90px]" />
      </div>

      <div className="relative z-10 flex w-full items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <div className="mb-8 flex justify-start">
            <Link
              to="/login"
              className="font-secondary inline-flex items-center rounded-sm px-2 py-2 text-[15px] text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <svg
                className="mr-2 h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to sign in
            </Link>
          </div>

          <div className="rounded-2xl shadow-xl bg-[#48484826] p-8 sm:p-10">
            <div className="mb-8 text-center">
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="mx-auto mb-6 h-10 w-auto sm:h-16"
              />
              <h2 className="font-title text-2xl font-bold tracking-tight text-white sm:text-[28px]">
                Reset your password
              </h2>
              <p className="font-secondary mt-2 text-[17px] leading-relaxed text-white/65">
                Enter your email and we&rsquo;ll send you a link to choose a new password.
              </p>
            </div>

            {submitted ? (
              isOauthOnly ? (
                <div
                  className="font-secondary rounded-sm border border-amber-500/25 bg-amber-500/10 px-4 py-4 text-sm text-amber-200"
                  role="status"
                >
                  <p className="font-semibold">This account uses Google sign-in.</p>
                  <p className="mt-1 text-amber-200/85">
                    <span className="font-semibold">{email}</span> was created via Google
                    OAuth and has no password to reset. Use the{" "}
                    <span className="font-semibold">Sign in with Google</span> button on
                    the login page instead.
                  </p>
                  <Link
                    to="/login"
                    className="font-secondary mt-3 inline-block text-amber-100 underline hover:text-white"
                  >
                    Go to sign in →
                  </Link>
                </div>
              ) : (
                <div
                  className="font-secondary rounded-sm border border-emerald-500/25 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-200"
                  role="status"
                >
                  <p className="font-semibold">Check your inbox.</p>
                  <p className="mt-1 text-emerald-200/85">
                    If an account with that email exists, we&rsquo;ve sent a reset link to{" "}
                    <span className="font-semibold">{email}</span>. The link expires in one hour.
                  </p>
                </div>
              )
            ) : (
              <>
                {errors.submit && (
                  <div
                    className="font-secondary mb-6 rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                    role="alert"
                  >
                    {errors.submit}
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label
                      htmlFor="email"
                      className="font-secondary mb-2 block text-sm font-medium text-white/80"
                    >
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((prev) => ({ ...prev, email: undefined, submit: undefined }));
                      }}
                      required
                      autoComplete="email"
                      className={`font-secondary block w-full rounded-sm border px-4 py-3.5 text-[15px] text-white transition-colors placeholder:text-white/40 focus:outline-none focus:ring-1 ${
                        errors.email
                          ? "border-red-500/80 bg-red-500/10 focus:border-red-500 focus:ring-red-500/30"
                          : "border-[#4848481A] bg-[#1F1F1F] hover:border-white/25 focus:border-primary focus:ring-primary/30"
                      }`}
                      placeholder="Enter your email"
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-400">{errors.email}</p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    className="font-secondary mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                    whileHover={isLoading ? {} : { scale: 1.01 }}
                    whileTap={isLoading ? {} : { scale: 0.99 }}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#0a0a0a] border-t-transparent"
                          aria-hidden="true"
                        />
                        Sending…
                      </>
                    ) : (
                      "Send reset link"
                    )}
                  </motion.button>
                </form>
              </>
            )}

            <div className="mt-8 text-center">
              <p className="font-secondary text-[15px] text-white/65">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-primary transition-colors hover:text-primary-hover"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
